import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { MapStyles } from '@methanesat/maps';
import { GoogleBasemapNames, MapInterface, RASTER_COLORMAP_NAMES } from '../../../../types';

import {
    resetOgiNearby,
    setOgiNearby,
    zoomToPermianAreaEmissions,
    zoomToPermianOilAndGas,
    zoomToPermianPlumeEmissions,
    zoomToPermianTarget,
    zoomToPermianTargetBroad
} from '../emissionsActions';

/**
 * This file defines the slice for managing the state of the map interface. It includes state variables,
 * reducers, and actions related to the map's gesture handling, style, and various
 * UI elements such as filter chips and emission chips. The slice also handles extra reducers for
 * specific actions related to OGI features and zooming to different areas on the map.
 */

const initialMapInterface: MapInterface = {
    gestureHandling: undefined,
    mapStyle: MapStyles.satelliteMuted,
    mapName: GoogleBasemapNames.Roadmap,
    ui: {
        isAreaEmissionsChipExpanded: false,
        isOgiOperatorFilterExpanded: false,
        isPlumeEmissionsChipExpanded: false,
        isOilAndGasTypeChipExpanded: false,
        isTargetEmissionsChipExpanded: false
    },
    l4MSATColorScale: RASTER_COLORMAP_NAMES.msatWarmDefault
};

export const mapInterfaceSlice = createSlice({
    name: 'mapInterface',
    initialState: initialMapInterface,
    reducers: {
        setGestureHandling: (state, { payload }: PayloadAction<'none' | undefined | string>) => {
            state.gestureHandling = payload;
        },
        setEmissionsMapName: (state, { payload }) => {
            state.mapName = payload;
        },
        setL4MSATColorScale: (state, action: PayloadAction<RASTER_COLORMAP_NAMES>) => {
            state.l4MSATColorScale = action.payload;
        },
        toggleSingleChip: (state, { payload }: { payload: keyof MapInterface['ui'] }) => {
            const chipState = Object.keys(state.ui) as Array<keyof MapInterface['ui']>;
            chipState.forEach((key) => {
                state.ui[key] = key === payload;
            });
        },
        toggleAreaEmissionChip: (state, { payload }: PayloadAction<boolean>) => {
            state.ui.isAreaEmissionsChipExpanded = payload;
        },
        toggleOgiFilterChip: (state, { payload }: PayloadAction<boolean>) => {
            state.ui.isOgiOperatorFilterExpanded = payload;
        },
        toggleOgiInfrastructureChip: (state, { payload }: PayloadAction<boolean>) => {
            state.ui.isOilAndGasTypeChipExpanded = payload;
        },
        togglePlumeEmissionChip: (state, { payload }: PayloadAction<boolean>) => {
            state.ui.isPlumeEmissionsChipExpanded = payload;
        },
        toggleTargetEmissionChip: (state, { payload }: PayloadAction<boolean>) => {
            state.ui.isTargetEmissionsChipExpanded = payload;
        },
        toggleAllEmissionChips: (state, { payload }: PayloadAction<boolean>) => {
            state.ui.isAreaEmissionsChipExpanded = payload;
            state.ui.isPlumeEmissionsChipExpanded = payload;
            state.ui.isTargetEmissionsChipExpanded = payload;
        },
        toggleAllChips: (state, { payload }: PayloadAction<boolean>) => {
            state.ui.isAreaEmissionsChipExpanded = payload;
            state.ui.isOgiOperatorFilterExpanded = payload;
            state.ui.isOilAndGasTypeChipExpanded = payload;
            state.ui.isPlumeEmissionsChipExpanded = payload;
            state.ui.isTargetEmissionsChipExpanded = payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(resetOgiNearby, (state) => {
                state.gestureHandling = undefined;
            })
            .addCase(setOgiNearby, (state) => {
                state.gestureHandling = 'none';
            })
            .addCase(zoomToPermianAreaEmissions, (state) => {
                state.ui.isTargetEmissionsChipExpanded = false;
                state.ui.isAreaEmissionsChipExpanded = true;
                state.ui.isPlumeEmissionsChipExpanded = false;
                state.ui.isOilAndGasTypeChipExpanded = false;
            })
            .addCase(zoomToPermianPlumeEmissions, (state) => {
                state.ui.isTargetEmissionsChipExpanded = false;
                state.ui.isPlumeEmissionsChipExpanded = true;
                state.ui.isAreaEmissionsChipExpanded = false;
                state.ui.isOilAndGasTypeChipExpanded = false;
            })
            .addCase(zoomToPermianTarget, (state) => {
                state.ui.isTargetEmissionsChipExpanded = true;
                state.ui.isAreaEmissionsChipExpanded = false;
                state.ui.isPlumeEmissionsChipExpanded = false;
                state.ui.isOilAndGasTypeChipExpanded = false;
            })
            .addCase(zoomToPermianTargetBroad, (state) => {
                state.ui.isAreaEmissionsChipExpanded = false;
                state.ui.isOgiOperatorFilterExpanded = false;
                state.ui.isPlumeEmissionsChipExpanded = false;
                state.ui.isOilAndGasTypeChipExpanded = false;
            })
            .addCase(zoomToPermianOilAndGas, (state, action) => {
                const { filterExpanded } = action.payload;

                if (filterExpanded) {
                    state.ui.isOgiOperatorFilterExpanded = true;
                    state.ui.isOilAndGasTypeChipExpanded = false;
                } else {
                    state.ui.isOgiOperatorFilterExpanded = false;
                    state.ui.isOilAndGasTypeChipExpanded = true;
                }

                state.ui.isTargetEmissionsChipExpanded = false;
                state.ui.isPlumeEmissionsChipExpanded = false;
                state.ui.isAreaEmissionsChipExpanded = false;
            });
    }
});

export const {
    setGestureHandling,
    setEmissionsMapName,
    setL4MSATColorScale,
    toggleAreaEmissionChip,
    toggleOgiFilterChip,
    toggleOgiInfrastructureChip,
    togglePlumeEmissionChip,
    toggleTargetEmissionChip,
    toggleSingleChip,
    toggleAllEmissionChips,
    toggleAllChips
} = mapInterfaceSlice.actions;

export default mapInterfaceSlice.reducer;
